import React, { useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faEnvelope, faUser, faPhone, faBuilding, faLock } from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import AuthApiService from "../../ApiServices/AuthApiService";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";

export default function Signup() {
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const generateCaptcha = useCallback(() => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    return { a, b, answer: a + b };
  }, []);
  const [captcha, setCaptcha] = useState(() => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    return { a, b, answer: a + b };
  });
  const navigate = useNavigate();

  const refreshCaptcha = () => {
    setCaptcha(generateCaptcha());
    setCaptchaInput("");
    setCaptchaVerified(false);
  };

  const handleCaptchaInput = (e) => {
    const val = e.target.value;
    setCaptchaInput(val);
    setCaptchaVerified(parseInt(val, 10) === captcha.answer);
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleSignup = async (e) => {
    e.preventDefault();

    if (!captchaVerified) {
      toast.error("Please solve the CAPTCHA correctly.");
      return;
    }

    setLoading(true);

    const signupData = {
      username: document.getElementById('first-name').value,
      email: document.getElementById('email').value,
      password,
      firstName: document.getElementById('first-name').value,
      lastName: document.getElementById('last-name').value,
      company: document.getElementById('company').value,
      phone: document.getElementById('phone').value,
    };

    try {
      const result = await AuthApiService.signup(signupData);
      if (result.isSuccess) {
        toast.success("Account created successfully! Please log in.");
        navigate("/login");
      } else if (result.length > 0) {
        toast.error(result[0].errorMessage);

      } else {
        toast.error("Signup failed. Please try again.");
      }
    } catch (error) {
      console.error("Signup error:", error);
      toast.error("An error occurred during signup.");
    } finally {
      setLoading(false);
      refreshCaptcha();
    }
  };

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center px-4"
      initial={{ rotateY: 90, opacity: 0 }}
      animate={{ rotateY: 0, opacity: 1 }}
      transition={{ duration: 1, ease: "easeOut" }}
    >
      <motion.div
        className="backdrop-blur-xl bg-white/10 border border-white/20 shadow-2xl rounded-3xl p-10 w-full max-w-xl"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 120 }}
      >
        <h2 className="text-4xl font-bold text-white text-center mb-4">Create an Account</h2>
        <p className="text-center text-sm text-white/80 mb-6">
          Already have an account?{" "}
          <Link to="/" className="text-yellow-300 font-semibold hover:underline">
            Sign in
          </Link>
        </p>

        <form className="space-y-5" onSubmit={handleSignup}>
          <div className="grid grid-cols-2 gap-4">
            <InputWithIcon id="first-name" icon={faUser} placeholder="First Name" required />
            <InputWithIcon id="last-name" icon={faUser} placeholder="Last Name" required />
          </div>
          <InputWithIcon id="email" icon={faEnvelope} type="email" placeholder="Email" required />
          <InputWithIcon id="company" icon={faBuilding} placeholder="Company" required />
          <InputWithIcon id="phone" icon={faPhone} type="tel" placeholder="Phone" required />

          <div>
            <label className="block text-white mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                required
                className="w-full bg-white/20 text-white placeholder-white/70 px-4 py-3 rounded-lg border border-white/30 focus:ring-2 focus:ring-yellow-300 focus:outline-none"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white"
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </div>
          </div>

          <div className="bg-white/10 border border-white/30 rounded-lg p-4">
            <p className="text-white text-sm mb-2">Verify you're human</p>
            <div className="flex items-center gap-3">
              <span className="text-white font-semibold text-lg">{captcha.a} + {captcha.b} = ?</span>
              <input
                type="number"
                value={captchaInput}
                onChange={handleCaptchaInput}
                placeholder="Answer"
                className={`w-24 bg-white/20 text-white placeholder-white/60 px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 ${
                  captchaInput === ""
                    ? "border-white/30 focus:ring-yellow-300"
                    : captchaVerified
                    ? "border-green-400 focus:ring-green-400"
                    : "border-red-400 focus:ring-red-400"
                }`}
              />
              <button
                type="button"
                onClick={refreshCaptcha}
                className="text-white/70 hover:text-white text-xs underline"
              >
                Refresh
              </button>
              {captchaVerified && <span className="text-green-400 font-bold">✓</span>}
            </div>
          </div>

          <motion.button
            type="submit"
            disabled={loading || !captchaVerified}
            className={`w-full py-3 mt-4 bg-yellow-400 text-black rounded-xl font-bold tracking-wide hover:bg-yellow-300 transition duration-300 shadow-md ${loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            whileTap={{ scale: 0.95 }}
          >
            {loading ? "Signing Up..." : "Sign Up"}
          </motion.button>
        </form>
      </motion.div>
    </motion.div>
  );
}

function InputWithIcon({ id, icon, type = "text", placeholder, required }) {
  return (
    <div className="relative">
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        required={required}
        className="w-full bg-white/20 text-white placeholder-white/70 px-11 py-3 rounded-lg border border-white/30 focus:ring-2 focus:ring-yellow-300 focus:outline-none"
      />
      <FontAwesomeIcon icon={icon} className="absolute top-1/2 left-4 transform -translate-y-1/2 text-white/80" />
    </div>
  );
}
